import * as yup from 'yup';
import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import type { InputStore } from '@/features/Sparten/hau/useInputStore';
import { useHauInputStore } from '@/features/Sparten/hau/useInputStore';
import type { SpartenConfig } from '@/features/Sparten/types';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { formatYears } from '@/utils/formatters/years';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import boolean from '@/utils/formatters/boolean';
import { hauInputDehydrationDefinitions } from '@/features/Sparten/hau/inputDehydration';
import complex from '@/utils/formatters/complex';
import { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';
import { hauContractDehydrationDefinitions } from '@/features/Sparten/hau/contractDehydration';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import image from './assets/bg.jpg';
import { hauVersicherungsangabenConfig } from '@/features/Sparten/hau/versicherungsangabenConfig';
import { absicherungsGroupMapping } from '@/features/Sparten/hau/inputMappings/absicherungsGroup';
import { risikoortGroupMapping } from '@/features/Sparten/hau/inputMappings/risikoortGroup';
import { beitragsreduzierungsGroupMapping } from '@/features/Sparten/hau/inputMappings/beitragsreduzierungsGroup';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/hau',

  props: {
    name: 'Hausratversicherung',
    sparte: 'hau',
    image,
  },

  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    hasRisikoDEMVerCertificate: true,

    versicherungsangabenConfig: hauVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum'],
        versichertePerson: ['geburtsdatum'],
      },
    },

    profileFields: [
      'ceran',
      'demvRabattiert',
      'elementarversicherung',
      'fahrraddiebstahl',
      'glasversicherung',
      'selbstbeteiligung',
    ],

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ADDRESS],
        ],
      },
      copyClientFieldsToInputStore: {
        'adresse.plz': 'plz',
        'adresse.strasse': 'strasse',
        'adresse.hausnummer': 'hausnummer',
        'adresse.ort': 'ort',
      },
    },

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useHauInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          hauInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state: Record<string, unknown>) =>
      dehydrateRequestData(state, hauContractDehydrationDefinitions),

    getSortableAttributes: () => [
      SORTABLE_ATTRIBUTES.VERSICHERUNGSSUMME,
      SORTABLE_ATTRIBUTES.SELBSTBETEILIGUNG,
      SORTABLE_ATTRIBUTES.LAUFZEIT,
    ],

    overrideFeatureValueWhen: {
      rubrik6_bezeichner1: yup.object({
        elementarversicherung: yup.boolean().oneOf([true]).required(),
      }),
    },

    previousDamageComponent: () =>
      import(
        '@/features/Sparten/hau/sections/contract/ContractPreviousDamage.vue'
      ),

    inputMappingGrouping: [
      risikoortGroupMapping,
      absicherungsGroupMapping,
      beitragsreduzierungsGroupMapping,
    ],

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Versicherungssumme',
          fieldPath: 'versicherungssumme',
          formatter: (amount: number | string) => formatCurrency(amount, true),
        },
        {
          label: 'Elementarschäden',
          fieldPath: 'elementarversicherung',
          formatter: boolean,
          inputFieldAsFallback: true,
        },
        {
          label: 'Glasversicherung',
          fieldPath: 'glasversicherung',
          formatter: boolean,
          inputFieldAsFallback: true,
        },
        {
          label: 'Cerankochfelder',
          fieldPath: 'ceran',
          formatter: boolean,
          inputFieldAsFallback: true,
          requirement: 'glasversicherung',
        },
        {
          label: 'Fahrraddiebstahl',
          inputFieldPath: 'fahrraddiebstahl',
          fieldPath: 'fahrrad',
          inputFormatter: formatChoice(SIMPLE_CHOICE.FAHRRADDIEBSTAHL),
          formatter: complex,
          customFormatter: (val: unknown) => {
            if (typeof val === 'string') {
              if (val.includes(' Prozent')) {
                return val
                  .replace(' Prozent', '%')
                  .replace('Versicherungssumme', 'VS');
              }
              if (val.includes('EUR')) {
                return val.replaceAll('EUR', '€');
              }
            }

            return val;
          },
        },
        {
          label: 'Selbstbeteiligung',
          fieldPath: 'selbstbeteiligung',
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.SELBSTBETEILIGUNG),
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/hau/components/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('hau').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('hau').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('hau').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('hau').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,
  },

  // Wizard steps
  steps: {
    input: {
      component: () => import('@/features/Sparten/hau/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/hau/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/hau/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/hau/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};

export default config;
